import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  Container,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Divider,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Check as CheckIcon,
  CreditCard as CreditCardIcon,
  Autorenew as RefillIcon,
  ExpandMore as ExpandMoreIcon,
  LocalOffer as OfferIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getConfig, getStripePublishableKey, loadConfig, FREE_TRIAL_ENABLED } from '../services/config';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe, AddressElement } from '@stripe/react-stripe-js';
import ModelRatesTable from '../components/ModelRatesTable';
import RefillSettingsCard from '../components/RefillSettingsCard';
import PrivacyComplianceCard from '../components/PrivacyComplianceCard';

interface SubscriptionTier {
  tier_id: string;
  name: string;
  monthly_price_usd: number;
  base_credits: number;
  bonus_percent: number;
  bonus_credits: number;
  total_credits: number;
  description: string;
}

interface CustomerInfo {
  customer_id: string;
  stripe_customer_id: string | null;
  has_default_payment_method?: boolean;
  subscription: {
    subscription_id: string;
    tier_id: string;
    total_credits: number;
    status: string;
    period_start?: string;
    period_end: string;
    cancel_at_period_end?: boolean;
    pending_tier_id?: string | null;
    pending_tier_name?: string | null;
    refill_settings?: {
      enabled: boolean;
      amount: number;
      cost_usd: number;
    };
  } | null;
  refill_settings: {
    refill_amount: number;
    refill_interval: string;
    refill_unit: string;
  } | null;
}

interface CreditUsage {
  period_start: string;
  period_end: string;
  total_credits_used: number;
  percentage_of_allowance_used: number | null;
  run_count: number;
  credits_by_model: { [model: string]: { total_tokens: number; input_tokens: number; output_tokens: number; credits_used: number; run_count: number } };
  tests?: { credits_used: number; run_count: number };
}

const PRIVACY_GATED_TIERS = ['starter'];
const PRIVACY_UNLOCKED_TIERS = ['builder', 'pro', 'scale'];

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
  );
}

function SummaryRow({
  icon,
  label,
  value,
  action,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        height: '100%',
        borderRadius: 2,
        bgcolor: '#f8fafc',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {icon}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
      </Box>
      {action}
    </Box>
  );
}

function PlanFeature({ children, bold }: { children: React.ReactNode; bold?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <CheckIcon fontSize="small" color="success" sx={{ mt: '2px' }} />
      <Typography variant="body2" sx={{ fontWeight: bold ? 600 : 400 }}>
        {children}
      </Typography>
    </Box>
  );
}

// Lazy Stripe promise - will be initialized when config is loaded
let stripePromise: ReturnType<typeof loadStripe> | null = null;

async function getStripePromise() {
  if (!stripePromise) {
    // Ensure config is loaded first
    await loadConfig();
    const key = getStripePublishableKey() || '';
    stripePromise = loadStripe(key);
  }
  return stripePromise;
}

// Promo Modal Component
function PromoModal({
  open,
  onClose,
  onSubscribe,
}: {
  open: boolean;
  onClose: (dontShowAgain: boolean) => void;
  onSubscribe: () => void;
}) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleNotNow = () => {
    onClose(dontShowAgain);
  };

  const handleLetsDoIt = () => {
    onSubscribe();
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <OfferIcon />
        <span>Limited Time Offer!</span>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Get Your First Month Free! 🎉
        </Typography>
        <Typography variant="body1" paragraph>
          Use promo code <Chip label="1MONTHFREE" color="primary" size="small" sx={{ fontWeight: 'bold', mx: 0.5 }} /> on the <strong>Starter plan</strong> to get a full month of AI credits at no cost.
        </Typography>
        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            ✓ 1,900 AI credits included
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            ✓ Access to all AI models
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            ✓ Cancel anytime, no commitment
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ✓ No credit card charge for first month
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
          }
          label="Don't show this again"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleNotNow} variant="outlined">
          Not right now
        </Button>
        <Button 
          onClick={handleLetsDoIt} 
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          Let's do it!
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Card form component
function PaymentCardForm({
  loading,
  onSubmit,
  promoCode,
  onPromoCodeChange,
  promoCodeStatus,
  promoCodeDiscount,
  promoCodeValidating,
  onPromoCodeBlur,
  selectedTier,
  submitLabel,
}: {
  loading: boolean;
  onSubmit: (element: any) => Promise<void>;
  promoCode: string;
  onPromoCodeChange: (value: string) => void;
  promoCodeStatus: 'valid' | 'invalid' | 'empty' | null;
  promoCodeDiscount: { description: string; duration_description: string } | null;
  promoCodeValidating: boolean;
  onPromoCodeBlur: () => void;
  selectedTier: string | null;
  submitLabel: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    try {
      const { error: stripeError, paymentMethod } = await stripe!.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message || 'Payment error');
        return;
      }

      if (paymentMethod) {
        await onSubmit(paymentMethod.id);
      }
    } catch (err: any) {
      setError(err.message || 'Payment error');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 2 }}>
        <AddressElement
          options={{
            mode: 'billing',
            allowedCountries: ['US', 'CA', 'GB', 'AU', 'NZ', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'CH', 'AT', 'SE', 'NO', 'DK', 'FI', 'PL', 'CZ', 'HU', 'RO', 'JP', 'SG', 'HK', 'IN', 'MX', 'BR', 'AR', 'ZA'],
          autocomplete: {
            mode: 'automatic',
          },
        }}
        onChange={() => {}}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: '16px',
                color: '#424242',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </Box>
      {FREE_TRIAL_ENABLED && selectedTier && (
        <Box sx={{ mb: 2 }}>
          {selectedTier === 'starter' && (
            <Alert severity="success" sx={{ mb: 2, backgroundColor: 'rgba(46, 125, 50, 0.08)' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                🎉 Enter code <strong>1MONTHFREE</strong> to try it out!
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="Promo Code (Optional)"
              placeholder={selectedTier === 'starter' ? "Enter 1MONTHFREE or other promo code" : "Not available for this plan"}
              value={promoCode}
              onChange={(e) => onPromoCodeChange(e.target.value)}
              size="small"
              disabled={selectedTier !== 'starter' || !selectedTier}
              error={promoCodeStatus === 'invalid'}
              helperText={
                promoCodeValidating
                  ? '⏳ Validating promo code...'
                  : selectedTier && selectedTier !== 'starter' && promoCode
                  ? '✗ Promo codes are only available for the Starter plan'
                  : promoCodeStatus === 'valid' && promoCodeDiscount
                  ? `✓ ${promoCodeDiscount.description} - ${promoCodeDiscount.duration_description}`
                  : promoCodeStatus === 'invalid'
                  ? '✗ Invalid or expired promo code'
                  : ''
              }
              sx={{
                '& .MuiFormHelperText-root': {
                  color: promoCodeStatus === 'valid' ? '#2e7d32' : promoCodeStatus === 'invalid' || (selectedTier && selectedTier !== 'starter' && promoCode) ? '#d32f2f' : 'inherit',
                  fontSize: '0.875rem',
                  fontWeight: promoCodeStatus || (selectedTier && selectedTier !== 'starter' && promoCode) ? 600 : 400,
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={() => onPromoCodeBlur()}
              disabled={!promoCode.trim() || selectedTier !== 'starter' || !selectedTier || promoCodeValidating}
              sx={{ mt: 1 }}
            >
              {promoCodeValidating ? <CircularProgress size={20} /> : 'Check'}
            </Button>
          </Box>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button type="submit" variant="contained" disabled={loading || !stripe || !elements}>
          {loading ? <CircularProgress size={24} color="inherit" /> : submitLabel}
        </Button>
      </Box>
    </form>
  );
}

// Main subscription page component
function SubscriptionContent() {
  const { getIdToken, refreshSubscriptionStatus } = useAuth();
  const config = getConfig();
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState(false);
  const [creatingStripeCustomer, setCreatingStripeCustomer] = useState(false);
  const [creditUsage, setCreditUsage] = useState<CreditUsage | null>(null);
  const [refillSettings, setRefillSettings] = useState<{ enabled: boolean; amount: number; cost_usd: number } | null>(null);
  const [isDevMode, setIsDevMode] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeStatus, setPromoCodeStatus] = useState<'valid' | 'invalid' | 'empty' | null>(null);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState<{ description: string; duration_description: string } | null>(null);
  const [promoCodeValidating, setPromoCodeValidating] = useState(false);
  const [promoModalOpen, setPromoModalOpen] = useState(false);

  // Detect dev mode based on hostname
  useEffect(() => {
    const hostname = window.location.hostname;
    setIsDevMode(hostname.includes('localhost') || hostname.includes('dev'));
  }, []);

  // Check if promo modal should be shown for unsubscribed users
  useEffect(() => {
    // Don't show promo modal if free trial is disabled
    if (!FREE_TRIAL_ENABLED) return;
    
    if (!customerInfo) return;

    // Only show to unsubscribed users (or users with just the free tier)
    const isUnsubscribed = !customerInfo.subscription || 
                           customerInfo.subscription.tier_id === 'free' ||
                           customerInfo.subscription.status === 'canceled';

    if (!isUnsubscribed) return;

    // Check localStorage for preferences
    const dontShowAgain = localStorage.getItem('promoModal_dontShowAgain');
    if (dontShowAgain === 'true') return;

    const lastShownStr = localStorage.getItem('promoModal_lastShown');
    const now = new Date().getTime();

    if (lastShownStr) {
      const lastShown = parseInt(lastShownStr, 10);
      const hoursSinceLastShown = (now - lastShown) / (1000 * 60 * 60);
      
      // Show again after 12 hours
      if (hoursSinceLastShown >= 12) {
        setPromoModalOpen(true);
      }
    } else {
      // First time - show the modal
      setPromoModalOpen(true);
    }
  }, [customerInfo]);

  const handlePromoModalClose = (dontShowAgain: boolean) => {
    setPromoModalOpen(false);
    
    if (dontShowAgain) {
      localStorage.setItem('promoModal_dontShowAgain', 'true');
    } else {
      // Just dismissed - save timestamp
      const now = new Date().getTime();
      localStorage.setItem('promoModal_lastShown', now.toString());
    }
  };

  const handlePromoModalSubscribe = () => {
    setPromoModalOpen(false);
    
    // Find the starter tier and trigger subscription flow
    const starterTier = tiers.find(t => t.tier_id === 'starter');
    if (starterTier) {
      // Pre-fill the promo code in state for UI
      setPromoCode('1MONTHFREE');
      // Pass promo code directly to avoid React state batching race condition
      handleTierClick('starter', '1MONTHFREE');
    }
  };

  // Promo code validation against Stripe
  const validatePromoCode = async (code: string) => {
    if (!code.trim()) {
      setPromoCodeStatus('empty');
      setPromoCodeDiscount(null);
      return;
    }
    
    // Only allow promo code on starter plan
    if (selectedTier && selectedTier !== 'starter') {
      setPromoCodeStatus('invalid');
      setPromoCodeDiscount(null);
      return;
    }
    
    // Validate against backend/Stripe
    setPromoCodeValidating(true);
    try {
      const token = await getIdToken();
      const response = await fetch(
        `${config.apiBaseUrl}/api/v2/billing/validate-promo-code?code=${encodeURIComponent(code)}&tier_id=${selectedTier}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPromoCodeStatus('valid');
        setPromoCodeDiscount({
          description: data.description,
          duration_description: data.duration_description,
        });
      } else {
        setPromoCodeStatus('invalid');
        setPromoCodeDiscount(null);
      }
    } catch (err) {
      setPromoCodeStatus('invalid');
      setPromoCodeDiscount(null);
    } finally {
      setPromoCodeValidating(false);
    }
  };

  const handlePromoCodeBlur = () => {
    validatePromoCode(promoCode);
  };

  const handlePromoCodeChange = (value: string) => {
    setPromoCode(value);
    // Clear status and discount when user starts typing
    setPromoCodeStatus(null);
    setPromoCodeDiscount(null);
  };

  // Fetch tiers and customer info
  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();

      // Fetch tiers from billing API - public endpoint, no auth needed but we send it anyway
      const tiersRes = await fetch(`${config.apiBaseUrl}/api/v2/billing/subscription-tiers`);
      if (!tiersRes.ok) throw new Error('Failed to fetch tiers');
      const tiersData = await tiersRes.json();
      // billing.py returns { tiers: [...] } structure
      setTiers(tiersData.tiers || []);

      // Fetch customer info - requires auth
      const customerRes = await fetch(`${config.apiBaseUrl}/api/v2/customers/current`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!customerRes.ok) throw new Error('Failed to fetch customer info');
      const customerData = await customerRes.json();
      setCustomerInfo(customerData.data);
      
      // Extract refill settings from subscription if available
      if (customerData.data?.subscription?.refill_settings) {
        setRefillSettings({
          enabled: customerData.data.subscription.refill_settings.enabled ?? true,
          amount: customerData.data.subscription.refill_settings.amount ?? 500,
          cost_usd: (customerData.data.subscription.refill_settings.amount ?? 500) * 0.01,
        });
      } else {
        // Clear refill settings if no active subscription
        setRefillSettings(null);
      }

      // Fetch credit usage - requires auth
      const usageRes = await fetch(`${config.apiBaseUrl}/api/v2/analytics/credit-usage`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (usageRes.ok) {
        const usageData = await usageRes.json();
        setCreditUsage(usageData.data);
      }

      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStripeCustomer = async () => {
    try {
      setCreatingStripeCustomer(true);
      const token = await getIdToken();

      const res = await fetch(`${config.apiBaseUrl}/api/v2/customers/stripe/customer`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to create Stripe customer');
      await fetchData();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreatingStripeCustomer(false);
    }
  };

  const handleAddPaymentMethod = async (paymentMethodId: string) => {
    try {
      setSubscribing(true);
      const token = await getIdToken();

      const res = await fetch(
        `${config.apiBaseUrl}/api/v2/customers/stripe/payment-method?payment_method_id=${paymentMethodId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );

      if (!res.ok) throw new Error('Failed to add payment method');

      // After payment method is added, subscribe to tier
      // Note: promoCode state should be available here since user typed it in the form
      if (selectedTier) {
        await handleSubscribeTier(selectedTier);
      } else {
        setPaymentDialogOpen(false);
        await fetchData();
        setError(null);
        setSuccessMessage('Card saved. We\'ll use it for your plan and auto-refills from now on.');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubscribing(false);
    }
  };

  const formatPeriodEnd = (value?: string | null) => {
    if (!value) return 'the end of this billing period';
    return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleSubscribeTier = async (tierId: string, promoCodeOverride?: string) => {
    try {
      setSubscribing(true);
      const token = await getIdToken();

      // Use override if provided (fixes race condition), otherwise use state
      const codeToUse = promoCodeOverride !== undefined ? promoCodeOverride : promoCode;

      const query = new URLSearchParams({ tier_id: tierId });
      // The API reads promo_code from the query string.
      if (codeToUse) query.set('promo_code', codeToUse);
      const res = await fetch(`${config.apiBaseUrl}/api/v2/customers/subscribe?${query}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: codeToUse ? JSON.stringify({
          promo_code: codeToUse
        }) : undefined,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || body?.message || 'Failed to subscribe');
      }
      const periodEndForMessage = customerInfo?.subscription?.period_end;
      const payload = await res.json().catch(() => null);
      const changeKind = payload?.data?.change_kind;
      setPaymentDialogOpen(false);
      setSelectedTier(null);
      setPromoCode('');
      setPromoCodeStatus(null);
      await fetchData();
      setError(null);
      if (changeKind === 'upgraded') {
        setSuccessMessage('Plan upgraded. Your billing date stayed the same; you were charged the prorated difference, and this period\'s usage still counts toward the new limit.');
      } else if (changeKind === 'downgraded') {
        setSuccessMessage(`You'll keep your current plan and credits until ${formatPeriodEnd(periodEndForMessage)}. The new price starts next period.`);
      } else if (changeKind === 'unchanged') {
        setSuccessMessage('Your subscription will continue.');
      } else {
        setSuccessMessage(null);
      }
      refreshSubscriptionStatus(); // Refresh global state after successful subscription
    } catch (err: any) {
      setSuccessMessage(null);
      setError(err.message);
    } finally {
      setSubscribing(false);
    }
  };

  const handleTierClick = async (tierId: string, promoCodeOverride?: string) => {
    const selected = tiers.find((t) => t.tier_id === tierId);
    const current = tiers.find((t) => t.tier_id === customerInfo?.subscription?.tier_id);
    if (customerInfo?.subscription && selected && current && selected.tier_id !== current.tier_id) {
      const periodEnd = formatPeriodEnd(customerInfo.subscription.period_end);
      if (selected.total_credits > current.total_credits) {
        const ok = window.confirm(
          `Upgrade to ${selected.name}?\n\nYour billing date stays the same. You'll be charged the prorated difference for the rest of this period, and your credit limit increases immediately. Credits already used this period still count toward the new limit.`
        );
        if (!ok) return;
      } else if (selected.total_credits < current.total_credits) {
        const ok = window.confirm(
          `Switch to ${selected.name} at period end?\n\nYou'll keep your current plan and credits until ${periodEnd}. After that you'll move to ${selected.name} at the new price. Unused time this period is not refunded.`
        );
        if (!ok) return;
      }
    }

    setSelectedTier(tierId);

    // If no Stripe customer, create one first
    if (!customerInfo?.stripe_customer_id) {
      try {
        await handleCreateStripeCustomer();
        // Open payment dialog after customer is created
        setTimeout(() => setPaymentDialogOpen(true), 500);
      } catch (err) {
        setError('Failed to create Stripe customer');
      }
    } else if (customerInfo?.has_default_payment_method) {
      // Customer has payment method on file, subscribe directly
      // Pass promo code override to avoid React state batching race condition
      await handleSubscribeTier(tierId, promoCodeOverride);
    } else {
      // No payment method, open dialog to add one
      setPaymentDialogOpen(true);
    }
  };

  const handleCancelSubscription = async () => {
    const periodEnd = formatPeriodEnd(customerInfo?.subscription?.period_end);
    const ok = window.confirm(
      `Cancel at the end of this billing period?\n\nYou'll keep access and remaining credits until ${periodEnd}. You won't be charged again after that.`
    );
    if (!ok) return;

    try {
      setSubscribing(true);
      const token = await getIdToken();

      const res = await fetch(
        `${config.apiBaseUrl}/api/v2/customers/cancel-subscription?at_period_end=true`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || body?.message || 'Failed to cancel subscription');
      }
      await fetchData();
      setError(null);
      setSuccessMessage(`Your plan stays active until ${periodEnd}. After that it will cancel.`);
      refreshSubscriptionStatus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubscribing(false);
    }
  };

  const handleKeepSubscription = async () => {
    const tierId = customerInfo?.subscription?.tier_id;
    if (!tierId) return;
    await handleSubscribeTier(tierId);
  };

  const handleUpdateCard = () => {
    setSelectedTier(null);
    setPaymentDialogOpen(true);
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: true });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const activeTierId = customerInfo?.subscription?.tier_id || null;
  const matchedTier = activeTierId ? tiers.find((t) => t.tier_id === activeTierId) : null;
  const subscriptionCredits = customerInfo?.subscription?.total_credits ?? 0;
  const subscriptionStatus = customerInfo?.subscription?.status ?? null;
  const hasSubscriptionRecord = !!customerInfo?.subscription;
  const isIncludedPackagedPlan = !!(
    hasSubscriptionRecord &&
    !matchedTier &&
    activeTierId !== 'free' &&
    (subscriptionCredits > 0 || subscriptionStatus === 'active' || subscriptionStatus === 'trialing')
  );
  const displayedPlanName = matchedTier?.name || (isIncludedPackagedPlan ? 'Included Packaged Plan' : (activeTierId || 'Active Subscription'));

  const subscription = customerInfo?.subscription ?? null;
  const isFreeCredits = activeTierId === 'free';
  const isCanceled = subscriptionStatus === 'canceled';
  const canSubscribe = !subscription || isFreeCredits || isCanceled;
  const hasPaidPlan = !!subscription && !canSubscribe;
  const planName = isFreeCredits ? 'Free credits' : displayedPlanName;
  const periodEndLabel = subscription?.period_end ? formatPeriodEnd(subscription.period_end) : undefined;
  const busy = subscribing || creatingStripeCustomer;

  const totalCredits = subscription?.total_credits ?? 0;
  const creditsUsed = creditUsage ? creditUsage.total_credits_used : null;
  const creditsLeft = creditsUsed !== null ? Math.max(totalCredits - creditsUsed, 0) : totalCredits;
  const usedPercent = creditsUsed !== null && totalCredits > 0 ? Math.min((creditsUsed / totalCredits) * 100, 100) : 0;
  const refillAddedCredits = matchedTier && totalCredits > matchedTier.total_credits ? totalCredits - matchedTier.total_credits : 0;
  const formatCredits = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

  const planStatus: { label: string; color: 'success' | 'warning' | 'info' | 'error' | 'default' } =
    subscription?.cancel_at_period_end
      ? { label: `Ends ${periodEndLabel ?? 'soon'}`, color: 'warning' }
      : subscriptionStatus === 'active'
      ? { label: 'Active', color: 'success' }
      : subscriptionStatus === 'trialing'
      ? { label: 'Trial', color: 'info' }
      : subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid'
      ? { label: 'Payment overdue', color: 'error' }
      : { label: (subscriptionStatus || 'inactive').replace(/_/g, ' '), color: 'default' };

  const priceText = matchedTier ? `$${matchedTier.monthly_price_usd}` : isIncludedPackagedPlan ? 'Included' : null;
  const renewalText = isFreeCredits
    ? 'One-time signup credits. Choose a plan below to get credits every month.'
    : !periodEndLabel
    ? null
    : isCanceled
    ? `Plan ended on ${periodEndLabel}.`
    : subscription?.cancel_at_period_end
    ? `Ends on ${periodEndLabel}. You won't be charged again.`
    : isIncludedPackagedPlan
    ? `Credits renew on ${periodEndLabel}.`
    : `Renews on ${periodEndLabel}. Credits reset then.`;
  const planActionLabel = isIncludedPackagedPlan ? null : canSubscribe ? 'Choose a plan' : 'Change plan';

  const currentRefill = refillSettings || { enabled: true, amount: 500, cost_usd: 5.0 };
  const refillUnavailableReason = subscription?.subscription_id
    ? undefined
    : 'Auto-refill becomes available once you choose a paid plan.';
  const refillSummary = refillUnavailableReason
    ? 'Available on paid plans'
    : currentRefill.enabled
    ? `On · blocks of ${formatCredits(currentRefill.amount)} credits ($${(currentRefill.amount * 0.01).toFixed(2)} each)`
    : 'Off · AI requests stop when credits run out';
  const hasCard = !!customerInfo?.has_default_payment_method;
  const selectedTierName = selectedTier ? tiers.find((t) => t.tier_id === selectedTier)?.name : undefined;

  // Privacy & Compliance tier gating (UX only — the backend is the source of truth):
  //   no subscription / inactive -> locked, Starter -> locked, Builder/Pro/Scale -> unlocked.
  const hasActivePaidSub =
    !!subscription &&
    (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') &&
    (subscription.total_credits ?? 0) > 0;
  const privacyTierUnlocks = !!activeTierId && PRIVACY_UNLOCKED_TIERS.includes(activeTierId);
  const privacyTierLocks = !!activeTierId && PRIVACY_GATED_TIERS.includes(activeTierId);
  const privacyEnabled = hasActivePaidSub && privacyTierUnlocks;
  let privacyReason: string | undefined;
  if (!hasActivePaidSub) {
    privacyReason =
      'Privacy & Compliance controls are available on paid plans. ' +
      'Subscribe to a Builder, Pro, or Scale plan above to unlock.';
  } else if (privacyTierLocks) {
    privacyReason =
      'The Starter plan does not include Privacy & Compliance controls. ' +
      'Upgrade to Builder, Pro, or Scale above to enable Zero Data ' +
      'Retention and configurable run-history retention.';
  } else if (!privacyTierUnlocks) {
    privacyReason =
      'Your current plan does not include Privacy & Compliance controls. ' +
      'Switch to a Builder, Pro, or Scale plan to unlock them.';
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Billing &amp; Plan</Typography>
        <Typography variant="body2" color="text.secondary">
          What you pay, how many credits you have left, and your auto-refill and card settings.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          {subscription ? (
            <>
              {isIncludedPackagedPlan && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {(subscription.total_credits ?? 0) > 1
                    ? `Your platform purchase includes ${subscription.total_credits?.toLocaleString()} credits per month at no additional charge.`
                    : 'This account is on an included packaged plan from an external subscription. Credits are syncing from billing.'}
                </Alert>
              )}
              {subscription.cancel_at_period_end && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={handleKeepSubscription} disabled={busy}>
                      Keep my plan
                    </Button>
                  }
                >
                  This plan stays active until {formatPeriodEnd(subscription.period_end)}, then it will cancel. You keep your remaining credits until then.
                </Alert>
              )}
              {subscription.pending_tier_id && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Switching to {subscription.pending_tier_name || subscription.pending_tier_id} on {formatPeriodEnd(subscription.period_end)}. You'll keep your current credit limit until then.
                </Alert>
              )}

              <Grid container spacing={4}>
                <Grid item xs={12} md={5}>
                  <Typography variant="overline" color="text.secondary">
                    Your plan
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                      {planName}
                    </Typography>
                    <Chip size="small" label={planStatus.label} color={planStatus.color} sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                  </Box>
                  {priceText && (
                    <Typography component="div" sx={{ mt: 1 }}>
                      <Box component="span" sx={{ fontSize: '2rem', fontWeight: 700 }}>
                        {priceText}
                      </Box>
                      {matchedTier && (
                        <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                          / month
                        </Box>
                      )}
                    </Typography>
                  )}
                  {renewalText && (
                    <Typography variant="body2" color="text.secondary">
                      {renewalText}
                    </Typography>
                  )}
                  {isIncludedPackagedPlan && activeTierId && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Package ID: {activeTierId}
                    </Typography>
                  )}
                  {planActionLabel && (
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => scrollToSection('plans')}>
                      {planActionLabel}
                    </Button>
                  )}
                </Grid>

                <Grid item xs={12} md={7}>
                  <Typography variant="overline" color="text.secondary">
                    Credits this period
                  </Typography>
                  <Typography component="div">
                    <Box component="span" sx={{ fontSize: '2rem', fontWeight: 700 }}>
                      {formatCredits(creditsLeft)}
                    </Box>
                    <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                      of {formatCredits(totalCredits)} left
                    </Box>
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={usedPercent}
                    color={usedPercent >= 90 ? 'warning' : 'primary'}
                    sx={{ height: 10, borderRadius: 5, my: 1.5, bgcolor: '#e2e8f0' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {creditsUsed !== null
                      ? `${formatCredits(creditsUsed)} used (${Math.round(usedPercent)}%)`
                      : 'Usage is not available right now.'}
                  </Typography>
                  {refillAddedCredits > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Includes {formatCredits(refillAddedCredits)} credits added by auto-refill.
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SummaryRow
                    icon={<RefillIcon color="primary" />}
                    label="Auto-refill"
                    value={refillSummary}
                    action={
                      !refillUnavailableReason && (
                        <Button variant="outlined" size="small" onClick={() => scrollToSection('auto-refill')}>
                          Change
                        </Button>
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SummaryRow
                    icon={<CreditCardIcon color="primary" />}
                    label="Payment method"
                    value={hasCard ? 'Card on file' : 'No card on file'}
                    action={
                      !!customerInfo?.stripe_customer_id && (
                        <Button variant="outlined" size="small" onClick={handleUpdateCard} disabled={busy}>
                          {hasCard ? 'Update card' : 'Add card'}
                        </Button>
                      )
                    }
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Your plan
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  No plan yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose a plan below to get AI credits every month. You can change or cancel anytime.
                </Typography>
              </Box>
              <Button variant="contained" onClick={() => scrollToSection('plans')}>
                See plans
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {subscription && (
        <Box id="auto-refill" sx={{ scrollMarginTop: 88 }}>
          <RefillSettingsCard
            subscriptionId={subscription.subscription_id}
            initialSettings={currentRefill}
            stripeCustomerId={customerInfo?.stripe_customer_id || ''}
            onUpdate={(settings) => {
              setRefillSettings(settings);
            }}
            unavailableReason={refillUnavailableReason}
            periodEndLabel={periodEndLabel}
          />
        </Box>
      )}

      <Box id="plans" sx={{ mb: 3, scrollMarginTop: 88 }}>
        <SectionHeader
          title="Plans"
          description={
            isIncludedPackagedPlan
              ? 'Your credits come with your platform purchase, so plans can’t be changed here.'
              : 'Every plan renews monthly. Upgrades start right away (you pay the difference for the rest of this period). Downgrades start on your next billing date.'
          }
        />

        {isDevMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              🧪 <strong>Test Mode Enabled</strong> - Subscribing to any plan will not result in billing. Use this to test the subscription flow.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {tiers.map((tier) => {
            const isCurrentTier = !canSubscribe && activeTierId === tier.tier_id;
            const currentTierCredits = matchedTier?.total_credits || 0;
            const canChange = hasPaidPlan && !!matchedTier;
            const canUpgrade = canChange && tier.total_credits > currentTierCredits;
            const canDowngrade = canChange && tier.total_credits < currentTierCredits;
            const isPendingTier = subscription?.pending_tier_id === tier.tier_id;
            const cancelAtPeriodEnd = !!subscription?.cancel_at_period_end;
            const showSpinner = busy && (selectedTier ? selectedTier === tier.tier_id : isCurrentTier);
            const spinner = <CircularProgress size={24} color="inherit" />;

            return (
              <Grid item xs={12} sm={6} md={3} key={tier.tier_id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2px solid',
                    borderColor: isCurrentTier ? 'primary.main' : 'transparent',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tier.name}
                      </Typography>
                      {isCurrentTier && <Chip label="Your plan" color="primary" size="small" />}
                      {isPendingTier && <Chip label="Next plan" color="info" size="small" />}
                    </Box>

                    <Typography component="div">
                      <Box component="span" sx={{ fontSize: '2rem', fontWeight: 700 }}>
                        ${tier.monthly_price_usd}
                      </Box>
                      <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                        / month
                      </Box>
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={1}>
                      <PlanFeature bold>{tier.total_credits.toLocaleString()} credits every month</PlanFeature>
                      {tier.bonus_credits > 0 && (
                        <PlanFeature>
                          Includes {tier.bonus_credits.toLocaleString()} bonus credits ({tier.bonus_percent}% extra)
                        </PlanFeature>
                      )}
                      <PlanFeature>Optional auto-refill</PlanFeature>
                      {PRIVACY_UNLOCKED_TIERS.includes(tier.tier_id) && (
                        <PlanFeature>Privacy &amp; Compliance controls</PlanFeature>
                      )}
                    </Stack>
                  </CardContent>

                  <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                    {isCurrentTier ? (
                      cancelAtPeriodEnd ? (
                        <Button fullWidth variant="contained" onClick={handleKeepSubscription} disabled={busy}>
                          {showSpinner ? spinner : 'Keep this plan'}
                        </Button>
                      ) : (
                        <Button fullWidth variant="outlined" color="error" onClick={handleCancelSubscription} disabled={busy}>
                          {showSpinner ? spinner : 'Cancel plan'}
                        </Button>
                      )
                    ) : isPendingTier ? (
                      <Button fullWidth variant="outlined" disabled>
                        Starts {periodEndLabel ?? 'next period'}
                      </Button>
                    ) : canSubscribe || canUpgrade || canDowngrade ? (
                      <Button
                        fullWidth
                        variant={canDowngrade ? 'outlined' : 'contained'}
                        onClick={() => handleTierClick(tier.tier_id)}
                        disabled={busy}
                      >
                        {showSpinner
                          ? spinner
                          : canUpgrade
                          ? `Upgrade to ${tier.name}`
                          : canDowngrade
                          ? `Switch to ${tier.name}`
                          : `Choose ${tier.name}`}
                      </Button>
                    ) : (
                      <Button fullWidth variant="outlined" disabled>
                        Not available
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {subscription && creditUsage && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader
              title="Usage this period"
              description={`Credits used by each AI model since ${formatPeriodEnd(creditUsage.period_start)}.`}
            />
            {Object.keys(creditUsage.credits_by_model).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No AI usage yet this period.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell>Model</TableCell>
                      <TableCell align="right">Input tokens</TableCell>
                      <TableCell align="right">Output tokens</TableCell>
                      <TableCell align="right">Credits used</TableCell>
                      <TableCell align="right">Runs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(creditUsage.credits_by_model).map(([model, data]) => (
                      <TableRow key={model} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{model}</TableCell>
                        <TableCell align="right">{(data.input_tokens || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{(data.output_tokens || 0).toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {data.credits_used.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">{data.run_count || 0}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: '#f8fafc', '& td': { fontWeight: 700 } }}>
                      <TableCell>Total</TableCell>
                      <TableCell align="right">
                        {Object.values(creditUsage.credits_by_model)
                          .reduce((sum, data) => sum + (data.input_tokens || 0), 0)
                          .toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        {Object.values(creditUsage.credits_by_model)
                          .reduce((sum, data) => sum + (data.output_tokens || 0), 0)
                          .toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>
                        {creditUsage.total_credits_used.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">{creditUsage.run_count || 0}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!!creditUsage.tests?.run_count && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Includes {creditUsage.tests.credits_used.toFixed(2)} credits from {creditUsage.tests.run_count.toLocaleString()} test
                runs (test cases and their AI judging).
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Promo Modal for Unsubscribed Users (only if free trial enabled) */}
      {FREE_TRIAL_ENABLED && (
        <PromoModal
          open={promoModalOpen}
          onClose={handlePromoModalClose}
          onSubscribe={handlePromoModalSubscribe}
        />
      )}

      {/* Payment Method Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setSelectedTier(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { minHeight: '600px' } }}
      >
        <DialogTitle>{selectedTier ? 'Add a card to subscribe' : hasCard ? 'Update payment card' : 'Add payment card'}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedTier
              ? `Enter your card details to subscribe to ${selectedTierName ?? 'this plan'}.`
              : 'This card becomes your default for your plan and auto-refills.'}
          </Typography>
          {isDevMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                📋 Dev/Test Mode
              </Typography>
              <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                <strong>Card Number:</strong> 4242 4242 4242 4242
              </Typography>
              <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                <strong>Expiry:</strong> Any future date
              </Typography>
              <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                <strong>CVC:</strong> Any 3 digits
              </Typography>
              <Typography variant="caption" component="div">
                <strong>ZIP:</strong> Any 5 digits
              </Typography>
            </Alert>
          )}
          <PaymentCardForm
            loading={subscribing}
            onSubmit={handleAddPaymentMethod}
            promoCode={promoCode}
            onPromoCodeChange={handlePromoCodeChange}
            promoCodeStatus={promoCodeStatus}
            promoCodeDiscount={promoCodeDiscount}
            promoCodeValidating={promoCodeValidating}
            onPromoCodeBlur={handlePromoCodeBlur}
            selectedTier={selectedTier}
            submitLabel={selectedTier ? `Subscribe to ${selectedTierName ?? 'plan'}` : 'Save card'}
          />
        </DialogContent>
      </Dialog>

      <PrivacyComplianceCard disabled={!privacyEnabled} disabledReason={privacyReason} />

      <Accordion
        disableGutters
        slotProps={{ transition: { unmountOnExit: true } }}
        sx={{ borderRadius: '12px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Model rates
            </Typography>
            <Typography variant="body2" color="text.secondary">
              How many credits each AI model uses, so you can compare costs.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pb: 3 }}>
          <ModelRatesTable title="" />
        </AccordionDetails>
      </Accordion>
    </Container>
  );
}

// Main export with Stripe provider
export default function Subscription() {
  return (
    <Elements stripe={getStripePromise()}>
      <SubscriptionContent />
    </Elements>
  );
}
